import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Ticket, Calendar, MapPin, Clock, QrCode, Download, CheckCircle, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format, parseISO, isPast, isToday } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyTickets = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      fetchTickets();
    }
  }, [user, authLoading, navigate]);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/my-tickets`);
      setTickets(response.data.tickets);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowQRModal(true);
  };

  const handleDownloadQR = () => {
    if (!selectedTicket) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${selectedTicket.qr_code}`;
    link.download = `ticket-${selectedTicket.event.title.replace(/\s+/g, '-')}.png`;
    link.click();
    toast.success('QR code downloaded');
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Event Ticket - ${selectedTicket?.event.title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; text-align: center; }
            .ticket { border: 2px solid #C6A87C; padding: 30px; max-width: 400px; margin: 0 auto; }
            .logo { color: #C6A87C; font-size: 24px; margin-bottom: 20px; }
            .title { font-size: 20px; font-weight: bold; margin-bottom: 15px; }
            .details { text-align: left; margin: 20px 0; font-size: 14px; }
            .qr { margin: 20px 0; }
            .qr img { width: 200px; height: 200px; }
            .code { font-family: monospace; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="ticket">
            <div class="logo">AgeForward Society</div>
            <div class="title">${selectedTicket?.event.title}</div>
            <div class="details">
              <p><strong>Date:</strong> ${selectedTicket?.event.date}</p>
              <p><strong>Time:</strong> ${selectedTicket?.event.start_time} - ${selectedTicket?.event.end_time}</p>
              <p><strong>Location:</strong> ${selectedTicket?.event.location}</p>
              <p><strong>Address:</strong> ${selectedTicket?.event.address}</p>
            </div>
            <div class="qr">
              <img src="data:image/png;base64,${selectedTicket?.qr_code}" alt="QR Code" />
            </div>
            <div class="code">Check-in Code: ${selectedTicket?.checkin_code}</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getEventStatus = (date) => {
    const eventDate = parseISO(date);
    if (isToday(eventDate)) return { label: 'Today', color: 'bg-green-100 text-green-800' };
    if (isPast(eventDate)) return { label: 'Past', color: 'bg-gray-100 text-gray-600' };
    return { label: 'Upcoming', color: 'bg-blue-100 text-blue-800' };
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30 py-12">
      <div className="container-custom max-w-4xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-8">
          <Ticket className="w-8 h-8 text-gold" />
          <div>
            <h1 className="text-3xl font-semibold">My Tickets</h1>
            <p className="text-muted-foreground">View and manage your event registrations</p>
          </div>
        </div>

        {tickets.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Ticket className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-xl mb-2">No Tickets Yet</h3>
              <p className="text-muted-foreground mb-6">
                Browse events and register to see your tickets here
              </p>
              <Button 
                onClick={() => navigate('/events')}
                className="bg-gold text-primary hover:bg-gold/90"
              >
                Browse Events
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {tickets.map((ticket) => {
              const status = getEventStatus(ticket.event.date);
              return (
                <Card key={ticket.id} className={ticket.checked_in ? 'border-green-500/50' : ''}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Event Image */}
                      <div className="w-full md:w-32 h-24 md:h-32 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                        {ticket.event.image_url ? (
                          <img 
                            src={ticket.event.image_url} 
                            alt={ticket.event.title}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Calendar className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Event Details */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <Link 
                              to={`/events/${ticket.event.id}`}
                              className="text-xl font-semibold hover:text-gold transition-colors"
                            >
                              {ticket.event.title}
                            </Link>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${status.color}`}>
                                {status.label}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded bg-secondary">
                                {ticket.type === 'ticket' ? 'Paid Ticket' : 'RSVP'}
                              </span>
                              {ticket.checked_in && (
                                <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" /> Checked In
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground mt-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{format(parseISO(ticket.event.date), 'EEEE, MMMM d, yyyy')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{ticket.event.start_time} - {ticket.event.end_time}</span>
                          </div>
                          <div className="flex items-center gap-2 md:col-span-2">
                            <MapPin className="w-4 h-4" />
                            <span>{ticket.event.location}</span>
                          </div>
                        </div>

                        <div className="mt-4 flex gap-2">
                          <Button
                            onClick={() => handleViewTicket(ticket)}
                            className="bg-gold text-primary hover:bg-gold/90"
                            data-testid={`view-ticket-${ticket.id}`}
                          >
                            <QrCode className="w-4 h-4 mr-2" />
                            View Ticket
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/events/${ticket.event.id}`)}
                          >
                            Event Details
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* QR Code Modal */}
        <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center">Your Event Ticket</DialogTitle>
            </DialogHeader>
            {selectedTicket && (
              <div ref={printRef} className="text-center">
                <div className="border-2 border-gold rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">{selectedTicket.event.title}</h3>
                  
                  <div className="bg-white p-4 rounded-lg inline-block mb-4">
                    <img 
                      src={`data:image/png;base64,${selectedTicket.qr_code}`}
                      alt="QR Code"
                      className="w-48 h-48"
                    />
                  </div>

                  <div className="text-sm text-muted-foreground space-y-1 mb-4">
                    <p><strong>Date:</strong> {format(parseISO(selectedTicket.event.date), 'MMMM d, yyyy')}</p>
                    <p><strong>Time:</strong> {selectedTicket.event.start_time} - {selectedTicket.event.end_time}</p>
                    <p><strong>Location:</strong> {selectedTicket.event.location}</p>
                  </div>

                  <p className="text-xs text-muted-foreground font-mono">
                    Code: {selectedTicket.checkin_code}
                  </p>

                  {selectedTicket.checked_in && (
                    <div className="mt-4 p-2 bg-green-100 text-green-800 rounded-lg text-sm flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Checked in at {selectedTicket.checked_in_at ? format(parseISO(selectedTicket.checked_in_at), 'h:mm a') : 'N/A'}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-6 justify-center">
                  <Button onClick={handleDownloadQR} variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                  <Button onClick={handlePrint} className="bg-gold text-primary hover:bg-gold/90">
                    Print Ticket
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MyTickets;
